import { createStore, compose, applyMiddleware } from 'redux';
import * as Sentry from '@sentry/react';
import thunk from 'redux-thunk';

// Middlewares.
import { fetcherMiddleware } from '@exonet/jsonapi-fetcher';

// Get the reducers.
import reducers from './reducers';
import userConstants from './reducers/user/constants';
import { constants as appConstants } from './reducers/app';
import { constants as socketConstants } from './reducers/socket';

// The error actions to provide to the fethcer.
import errorActions from './actions/errorActions';

/**
 * @type {Function} Determine the composer.
 */
const composer =
  !__PRODUCTION__ &&
  typeof window !== 'undefined' &&
  typeof window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ !== 'undefined'
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    : compose;

/**
 * The state is attached to every captured error and each action becomes a breadcrumb, so both
 * are reduced to an allowlist: anything not named below is dropped. A denylist does not hold
 * here. The state is wider than the reducers suggest, the SSR hydration in `getInitialState`
 * merges the session into the `app` and `user` slices. The fetcher dispatches an action per API
 * call with the request and the response body attached, and spreads the response body over the
 * action itself on a failure.
 */

/**
 * @type {Object} The state keys that may be sent to Sentry, per slice. Slices that are not
 * listed here are dropped, and so is every key that is not listed for a slice.
 *
 * Deliberately absent:
 * - `api`, it caches whole API responses, including the created access token returned by
 *   `POST /access_tokens`.
 * - `socket.token`, it is the API access token of the current session.
 * - `otp.secret`, `otp.url` and `otp.source`, they are the TOTP enrolment secret, the
 *   `otpauth://` URL that embeds it and the QR image that encodes the same URL.
 * - `user.name`, `user.emailAddress` and `user.customers`, and `app.clientIp` and
 *   `app.clientIpScope`, they are personal data that an error report does not need.
 */
const allowedStateKeys = {
  app: [
    'canSwitchCustomer',
    'validCustomer',
    'sessionExpired',
    'maintenance',
    'error',
    'language',
    'theme',
    'autoSwitchTheme',
    'newVersion',
  ],
  user: ['id', 'isEmployee', 'verified', 'roles', 'permissionGroups'],
  otp: ['enabled', 'verified', 'skipped'],
  socket: ['connected'],
};

/**
 * @type {string[]} The keys of `app.customer` that may be sent to Sentry. The ID is enough to
 * tell which customer an error belongs to, the rest of the object is customer data.
 */
const allowedCustomerKeys = ['id'];

/**
 * @type {string[]} The keys of a fetcher action that may be sent to Sentry. Enough to see which
 * call was made and how it ended, without its contents: `result` is the response body,
 * `target.body` is the request body and `target.headers` the request headers, and a failed call
 * spreads the response body over the action itself.
 */
const allowedActionKeys = ['type', 'url', 'key', 'statusCode', 'timestamp'];

/**
 * @type {string[]} The keys of the contract of a fetcher action that may be sent to Sentry.
 */
const allowedTargetKeys = ['type', 'id'];

/**
 * @type {string[]} The action types whose payload may be sent to Sentry. Every other action is
 * reduced to its type, which keeps the breadcrumb trail readable without shipping a payload that
 * nobody has vetted: `USER/LOGIN` carries the whole login response (the API token, the user, the
 * customer list and the client IP address), `APP/SET_CUSTOMER` the whole customer and
 * `OTP/GENERATE` the TOTP enrolment secret.
 */
const allowedActionPayloads = [
  appConstants.setSessionExpired,
  appConstants.setMaintenance,
  appConstants.setError,
  appConstants.setLanguage,
  appConstants.setTheme,
  appConstants.setAutoSwitchTheme,
  appConstants.setNewVersion,
  socketConstants.connected,
  userConstants.verify,
];

/**
 * @type {string} The action type prefix used by every action the fetcher dispatches.
 */
const fetcherActionPrefix = 'jsonapi-fetcher/';

/**
 * Reduce an object to the given keys.
 *
 * @param {Object} source The object to reduce.
 * @param {string[]} keys The keys to keep.
 *
 * @returns {Object} An object containing only the allowed keys that are set on the source.
 */
const only = (source, keys) =>
  keys.reduce(
    (result, key) =>
      typeof source?.[key] === 'undefined' ? result : { ...result, [key]: source[key] },
    {},
  );

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    // Reduce the actions of the fetcher to the metadata of the call they describe.
    if (typeof action.type === 'string' && action.type.startsWith(fetcherActionPrefix)) {
      return {
        ...only(action, allowedActionKeys),
        target: only(action.target, allowedTargetKeys),
      };
    }

    // Keep the payload of the actions that are named, and only the type of the rest.
    return allowedActionPayloads.includes(action.type)
      ? { type: action.type, payload: action.payload }
      : { type: action.type };
  },
  stateTransformer: (state) => {
    const snapshot = Object.entries(allowedStateKeys).reduce(
      (result, [slice, keys]) => ({
        ...result,
        [slice]: only(state[slice], keys),
      }),
      {},
    );

    return {
      ...snapshot,
      app: {
        ...snapshot.app,
        customer: state.app?.customer ? only(state.app.customer, allowedCustomerKeys) : null,
      },
    };
  },
});

/**
 * Get the initial state of the app.
 *
 * @returns {Object} The initial state.
 */
export const getInitialState = () => {
  // Define the initialState, create a new object.
  const windowState = { ...(window.INITIAL_STATE || {}) };

  // Clean up the window.
  Object.keys(windowState)
    .filter((key) => typeof window.INITIAL_STATE[key] !== 'undefined')
    .forEach((key) => {
      delete window.INITIAL_STATE[key];
    });

  delete window.INITIAL_STATE;

  return windowState;
};

/**
 * Create the store.
 *
 * @param {Object} initialState The initial state of the store.
 * @param {Function} createClient The method to create the fetcher client.
 *
 * @returns {Store} The created redux store.
 */
export default (initialState, createClient) =>
  createStore(
    reducers,
    initialState,
    composer(
      applyMiddleware(thunk, fetcherMiddleware(createClient(), errorActions)),
      sentryReduxEnhancer,
    ),
  );
